import { useMemo } from 'react';
import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type CartesianChartProps,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import {
  chartBarGeometry,
  legendItems,
  type ChartSeries,
} from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { useLegendToggle } from '../../../molecules/ChartLegend/useLegendToggle';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type TopicOwnershipView = {
  /** `Topics we lead`, `Biggest gap`. */
  kpis: KPIMetric[];
  /**
   * One row per topic, most conversation first; one key per brand drawn, its
   * share (%) of the topic's whole conversation — every brand's, drawn or not.
   */
  rows: ChartRow[];
  /** The topic option value behind each row, for the drill-down. */
  keys: string[];
  xKey: string;
  /** The brands drawn, in option order. */
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** The count behind a share, quieter in the tooltip. */
  formatDetail?: CartesianChartProps['formatDetail'];
  /** Comparison-period reading per row and brand; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type TopicOwnershipCardProps = WidgetBlockProps & {
  view?: TopicOwnershipView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
  /**
   * Each legend entry shows or hides its brand (the executive view, GP
   * 2026-11-03). Only what is drawn changes: each segment stays a share of the
   * whole topic. Default off.
   */
  toggleable?: boolean;
};

/**
 * Each topic's band: a bar at its widest plus the same again around it, so
 * every topic keeps a full-weight bar however many there are.
 */
const ROW_HEIGHT = chartBarGeometry.maxBarSize * 2;

/** The share scale, pinned with its ticks (docs/design/rules.md §4). */
const SHARE_DOMAIN: [number, number] = [0, 100];
const SHARE_TICKS = [0, 20, 40, 60, 80, 100];
const percent = (value: number) => `${value}%`;

/**
 * Topic ownership — each topic's conversation split by brand, one bar per
 * topic (the report's §3, "who leads each topic", 2026-10-28). Not in Figma:
 * a horizontal stack with topics for rows and brands for segments, each
 * segment the brand's share of the topic's *whole* conversation on a pinned
 * 0–100% scale — so a bar of every brand fills it, and one of the brands
 * Brands picks stops short, the rest of the market being the gap. A normalised
 * stack would instead stretch the picked brands to fill the bar. The plot
 * grows with the topics rather than thinning them.
 */
export function TopicOwnershipCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
  toggleable = false,
}: TopicOwnershipCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  const keys = useMemo(() => view?.series.map((s) => s.key) ?? [], [view]);
  const legend = useLegendToggle(keys);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Topic ownership"
      helpText="Each topic's conversation split by brand: who leads it, and where we are under-represented."
      helpSupportingText="Each segment is a brand's share of everyone's conversation about the topic, so a bar of only the brands picked in Brands stops short of 100%. Most conversation first; a mention naming two brands counts for both."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            onViewDetails={onViewDetails}
            xKey={view.xKey}
            series={toggleable ? view.series.filter((s) => !legend.isOff(s.key)) : view.series}
            stacked
            orientation="horizontal"
            yDomain={SHARE_DOMAIN}
            yTicks={SHARE_TICKS}
            formatAxisValue={percent}
            height={Math.max(PLOT_HEIGHT, view.rows.length * ROW_HEIGHT)}
            legend="none"
            formatValue={view.formatValue}
            formatDetail={view.formatDetail}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={
        view && (
          <ChartLegend
            items={legendItems(view.series).map((item, i) => ({
              ...item,
              off: toggleable && legend.isOff(keys[i]!),
            }))}
            onToggle={toggleable ? (i) => legend.toggle(keys[i]!) : undefined}
          />
        )
      }
    />
  );
}
